import React from 'react';
import { Routes, Route, Navigate, useSearchParams, useLocation } from 'react-router-dom';
import { useSubdomain } from '../../hooks/useSubdomain';
import { useCustomDomainHost } from '../../hooks/useCustomDomainEvent';
import AuthContextManager from '../AuthContextManager';
import { applyHostBrandChrome } from '../../utils/brandFavicon';
import { useBrand } from '../../brands/BrandProvider';
import { devLog, IS_DEV } from '../../utils/devLog';
import { Box, CircularProgress, Typography } from '@mui/material';

// Layouts
import MainLayout from '../layout/MainLayout';
import AuthLayout from '../layout/AuthLayout';
import SuperAdminMainLayout from '../../features/master_adm/layout/SuperAdminMainLayout';

// Páginas públicas
import HomePage from '../../pages/public/HomePage';
import EventDetailsPage from '../../pages/public/EventDetailsPage';
import ParishRegistrationRequest from '../../pages/events/ParishRegistrationRequest';
import LoginPage from '../../pages/auth/LoginPage';
import SubParishLoginPage from '../../pages/auth/SubParishLoginPage';
import SectorAccessPage from '../../pages/auth/SectorAccessPage';
import SetPassword from '../../pages/auth/SetPassword';
import ForgotPasswordPage from '../../pages/auth/ForgotPasswordPage';
import ResetPasswordPage from '../../pages/auth/ResetPasswordPage';
import InvoiceDataRequestPage from '../../pages/invoice/InvoiceDataRequestPage';
import RegularizarPagarmeParishPage from '../../pages/public/RegularizarPagarmeParishPage';

// Páginas do dashboard
import DashboardPage from '../../pages/dashboard/DashboardPage';
import EventsListPage from '../../pages/dashboard/EventsListPage';
import EventWorkspacePage from '../../pages/dashboard/EventWorkspacePage';
import CreateEventPage from '../../pages/dashboard/CreateEventPage';
import SaquePage from '../../pages/dashboard/SaquePage';
import ProductsPage from '../../pages/dashboard/ProductsPage';
import AllProductsPage from '../../pages/dashboard/AllProductsPage';
import ProfilePage from '../../pages/dashboard/ProfilePage';
import ParishAdminOnboardingPage from '../../pages/dashboard/ParishAdminOnboardingPage';
import ParishLandingEditorPage from '../../pages/dashboard/ParishLandingEditorPage';
import InvoiceIssuancePage from '../../pages/dashboard/InvoiceIssuancePage';
import SubParishesPage from '../../pages/dashboard/SubParishesPage';
import AddOrganizationPage from '../../pages/dashboard/AddOrganizationPage';
import LandingPublicPage from '../../pages/landings/LandingPublicPage';

// Páginas de lojas
import CreateStorePage from '../../pages/stores/CreateStorePage';
import EditStorePage from '../../pages/stores/EditStorePage';
import StoreListPage from '../../pages/stores/StoreListPage';
import StorePublicPage from '../../pages/stores/StorePublicPage';
import StoreDashboardPage from '../../pages/stores/StoreDashboardPage';

// Páginas de eventos públicos
import EventRegistrationPage from '../../pages/events/EventRegistrationPage';
import EventPublicPage from '../../pages/events/EventPublicPage';
import SolicitacaoEnviadaPage from '../../pages/events/SolicitacaoEnviadaPage';
import ProductPurchasePage from '../../pages/events/ProductPurchasePage';
import PendingPaymentPage from '../../pages/events/PendingPaymentPage';
import EventCheckInPortalPage from '../../pages/events/EventCheckInPortalPage';

// Páginas de suporte
import EmailSupportPage from '../../pages/support/EmailSupportPage';
import EmailSuccessPage from '../../pages/support/EmailSuccessPage';
import AcutisInstitucionalPage from '../../pages/acutis/AcutisInstitucionalPage';
import PoliticaPrivacidadePage from '../../pages/acutis/PoliticaPrivacidadePage';
import FestojoDemo from '../../features/festejo/FestojoDemo';
import FestojoPublicPage from '../../features/festejo/FestojoPublicPage';
import FestojoFormContainer from '../../features/festejo/FestojoFormContainer';

// Páginas legais
import TermsOfService from '../../pages/legal/TermsOfService';
import PrivacyPolicy from '../../pages/legal/PrivacyPolicy';
import RefundPolicy from '../../pages/legal/RefundPolicy';
import ContractSignaturePage from '../../pages/legal/ContractSignaturePage';

import NovidadesPage from '../../features/audience-funnel/NovidadesPage';
import AudiencePublicoPage from '../../features/audience-funnel/AudiencePublicoPage';
import AudienceFiltrosPage from '../../features/audience-funnel/AudienceFiltrosPage';
import AudienceFunilPage from '../../features/audience-funnel/AudienceFunilPage';
import RequireAudienceFunnelBeta from '../../features/audience-funnel/RequireAudienceFunnelBeta';
import { AUDIENCE_FUNNEL_UI_ENABLED } from '../../features/audience-funnel/featureFlag';

// Páginas do Super Admin
import AdminLoginPage from '../../features/master_adm/page/AdminLoginPage';
import AdminDashboard from '../../features/master_adm/page/Dashboard';
import AdminEvents from '../../features/master_adm/page/Events';
import AdminParishes from '../../features/master_adm/page/Parishes';
import AdminProducerCrm from '../../features/master_adm/page/ProducerCrm';
import ParishPagarmeReview from '../../features/master_adm/page/ParishPagarmeReview';
import AdminPayouts from '../../features/master_adm/page/Payouts';
import BulkMessages from '../../features/master_adm/page/BulkMessages';
import InvoiceIssuance from '../../features/master_adm/page/InvoiceIssuance';
import Refunds from '../../features/master_adm/page/Refunds';
import Transactions from '../../features/master_adm/page/Transactions';
import TransactionsLayout from '../../features/master_adm/layout/TransactionsLayout';
import PaymentQueuesLayout from '../../features/master_adm/layout/PaymentQueuesLayout';
import DocumentationPage from '../../features/master_adm/page/DocumentationPage';
import CustomPageInvitesPage from '../../features/master_adm/page/CustomPageInvitesPage';
import AdminLegalContracts from '../../features/master_adm/page/AdminLegalContracts';
import AdminLedger from '../../features/master_adm/page/AdminLedger';
import AppParaApp from '../../features/master_adm/page/AppParaApp';
import AdminEditEventPage from '../../features/master_adm/page/AdminEditEventPage';
import AdminSettings from '../../features/master_adm/page/Settings';
import AdminReports from '../../features/master_adm/page/Reports';
import PaymentQueues from '../../features/master_adm/page/PaymentQueues';
import ContadoresAccessPage from '../../features/master_adm/page/ContadoresAccessPage';
import ContadorLoginPage from '../../features/master_adm/page/ContadorLoginPage';
import PersonalizedSignupPage from '../../pages/public/PersonalizedSignupPage';
import { useSelector } from 'react-redux';
import { selectUser } from '../../features/auth/authSlice';

// Rota protegida genérica
import { ProtectedRoute, ParishAdminRoute, SuperAdminRoute, RequirePermission } from './ProtectedRoutes';
import SaqueRouteGuard from './SaqueRouteGuard';

function LegacyPagarmeReviewRedirect() {
  const [searchParams] = useSearchParams();
  const parishId = searchParams.get('parishId');
  if (parishId) {
    return <Navigate to={`/parishes/pagarme/${encodeURIComponent(parishId)}`} replace />;
  }
  return <Navigate to="/parishes?tab=recipientApproval" replace />;
}

/** Contador só pode acessar emissão de nota. */
function ContadorOnlyGuard({ children }: { children: React.ReactNode }) {
  const user = useSelector(selectUser);
  const location = useLocation();
  if (
    user?.userType === 'contador' &&
    !location.pathname.includes('invoice-issuance')
  ) {
    return <Navigate to="/invoice-issuance" replace />;
  }
  return <>{children}</>;
}

/** Rotas do painel parish_admin (domínio da plataforma ou domínio custom da landing). */
function ParishAdminDashboardTree() {
  return (
    <ProtectedRoute>
      <MainLayout>
        <Routes>
          <Route index element={<DashboardPage />} />
          <Route path="perfil" element={<ProfilePage />} />
          <Route
            path="adicionar-organizacao"
            element={
              <ParishAdminRoute>
                <AddOrganizationPage />
              </ParishAdminRoute>
            }
          />
          <Route
            path="onboarding"
            element={
              <ParishAdminRoute>
                <ParishAdminOnboardingPage />
              </ParishAdminRoute>
            }
          />
          <Route
            path="eventos"
            element={
              <ParishAdminRoute>
                <EventsListPage />
              </ParishAdminRoute>
            }
          />
          <Route
            path="minha-pagina"
            element={
              <ParishAdminRoute>
                <ParishLandingEditorPage />
              </ParishAdminRoute>
            }
          />
          <Route
            path="eventos/novo"
            element={
              <ParishAdminRoute>
                <CreateEventPage />
              </ParishAdminRoute>
            }
          />
          <Route
            path="eventos/:eventId/editar"
            element={
              <ParishAdminRoute>
                <CreateEventPage />
              </ParishAdminRoute>
            }
          />
          <Route
            path="eventos/:eventId/workspace"
            element={
              <ParishAdminRoute>
                <EventWorkspacePage />
              </ParishAdminRoute>
            }
          />
          <Route
            path="saque"
            element={
              <ParishAdminRoute>
                <SaqueRouteGuard>
                  <SaquePage />
                </SaqueRouteGuard>
              </ParishAdminRoute>
            }
          />
          <Route
            path="produtos"
            element={
              <ParishAdminRoute>
                <ProductsPage />
              </ParishAdminRoute>
            }
          />
          <Route
            path="eventos/:eventId/produtos"
            element={
              <ParishAdminRoute>
                <AllProductsPage />
              </ParishAdminRoute>
            }
          />
          <Route
            path="emissao-nota"
            element={
              <ParishAdminRoute>
                <InvoiceIssuancePage />
              </ParishAdminRoute>
            }
          />
          <Route
            path="subparoquias"
            element={
              <ParishAdminRoute>
                <RequirePermission permission="MANAGE_SUBPARISHES">
                  <SubParishesPage />
                </RequirePermission>
              </ParishAdminRoute>
            }
          />
          <Route
            path="lojas"
            element={
              <ParishAdminRoute>
                <StoreListPage />
              </ParishAdminRoute>
            }
          />
          <Route
            path="lojas/nova"
            element={
              <ParishAdminRoute>
                <CreateStorePage />
              </ParishAdminRoute>
            }
          />
          <Route
            path="lojas/:storeId/editar"
            element={
              <ParishAdminRoute>
                <EditStorePage />
              </ParishAdminRoute>
            }
          />
          <Route
            path="lojas/:storeId/dashboard"
            element={
              <ParishAdminRoute>
                <StoreDashboardPage />
              </ParishAdminRoute>
            }
          />
          {AUDIENCE_FUNNEL_UI_ENABLED ? (
            <>
              <Route
                path="novidades"
                element={
                  <ParishAdminRoute>
                    <NovidadesPage />
                  </ParishAdminRoute>
                }
              />
              <Route
                path="publico"
                element={
                  <ParishAdminRoute>
                    <RequireAudienceFunnelBeta>
                      <AudiencePublicoPage />
                    </RequireAudienceFunnelBeta>
                  </ParishAdminRoute>
                }
              />
              <Route
                path="filtros"
                element={
                  <ParishAdminRoute>
                    <RequireAudienceFunnelBeta>
                      <AudienceFiltrosPage />
                    </RequireAudienceFunnelBeta>
                  </ParishAdminRoute>
                }
              />
              <Route
                path="funil"
                element={
                  <ParishAdminRoute>
                    <RequireAudienceFunnelBeta>
                      <AudienceFunilPage />
                    </RequireAudienceFunnelBeta>
                  </ParishAdminRoute>
                }
              />
            </>
          ) : null}
        </Routes>
      </MainLayout>
    </ProtectedRoute>
  );
}

const SubdomainRouter: React.FC = () => {
  const subdomain = useSubdomain();
  const brand = useBrand();
  const {
    loading: customDomainLoading,
    event: customDomainEvent,
    landing: customDomainLanding,
  } = useCustomDomainHost();

  devLog('Subdomain detectado:', subdomain);
  
  React.useEffect(() => {
    if (!IS_DEV) return;
    void import('../../utils/debugAuth').then((m) => m.debugAuthContext());
    void import('../../utils/testAuthContext').then((m) => m.testAuthContext());
  }, []);

  React.useEffect(() => {
    applyHostBrandChrome();
  }, [customDomainLanding, customDomainEvent]);

  // Domínio personalizado
  if (customDomainLoading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (customDomainLanding) {
    return (
      <AuthContextManager>
        <Routes>
          <Route path="login" element={<AuthLayout><LoginPage /></AuthLayout>} />
          <Route path="recuperar-senha" element={<AuthLayout><ForgotPasswordPage /></AuthLayout>} />
          <Route path="resetar-senha/:token" element={<AuthLayout><ResetPasswordPage /></AuthLayout>} />
          <Route path="definir-senha/:token" element={<AuthLayout><SetPassword /></AuthLayout>} />
          <Route path="cadastro-paroquia" element={<AuthLayout wideMain><ParishRegistrationRequest /></AuthLayout>} />
          <Route path="cadastro-personalizado" element={<PersonalizedSignupPage />} />
          <Route path="dashboard/*" element={<ParishAdminDashboardTree />} />
          <Route path="check-in" element={<EventCheckInPortalPage />} />
          <Route path="solicitacao-enviada" element={<MainLayout><SolicitacaoEnviadaPage /></MainLayout>} />
          <Route path="pagamento-pendente/:registrationId" element={<MainLayout><PendingPaymentPage /></MainLayout>} />
          <Route
            index
            element={
              brand.key === 'zuadaria' ? (
                <MainLayout>
                  <HomePage />
                </MainLayout>
              ) : (
                <LandingPublicPage initialData={customDomainLanding} />
              )
            }
          />
          <Route
            path="*"
            element={
              <MainLayout>
                <Routes>
                  <Route path=":eventSlug/inscricao" element={<EventRegistrationPage />} />
                  <Route path=":eventSlug/produto/:productId" element={<ProductPurchasePage />} />
                  <Route path=":eventSlug/suporte-email" element={<EmailSupportPage />} />
                  <Route path=":eventSlug/email-enviado" element={<EmailSuccessPage />} />
                  <Route path=":eventSlug" element={<EventPublicPage />} />
                  <Route
                    path="*"
                    element={
                      <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h6">Página não encontrada neste domínio</Typography>
                      </Box>
                    }
                  />
                </Routes>
              </MainLayout>
            }
          />
        </Routes>
      </AuthContextManager>
    );
  }

  if (customDomainEvent?.slug) {
    const slug = customDomainEvent.slug;
    return (
      <AuthContextManager>
        <Routes>
          <Route path="check-in" element={<EventCheckInPortalPage />} />
          <Route path="solicitacao-enviada" element={<MainLayout><SolicitacaoEnviadaPage /></MainLayout>} />
          <Route path="pagamento-pendente/:registrationId" element={<MainLayout><PendingPaymentPage /></MainLayout>} />
          <Route
            path="*"
            element={
              <MainLayout>
                <Routes>
                  <Route index element={<Navigate to={`/${slug}`} replace />} />
                  <Route path="inscricao" element={<Navigate to={`/${slug}/inscricao`} replace />} />
                  <Route path=":eventSlug/inscricao" element={<EventRegistrationPage />} />
                  <Route path=":eventSlug/produto/:productId" element={<ProductPurchasePage />} />
                  <Route path=":eventSlug/suporte-email" element={<EmailSupportPage />} />
                  <Route path=":eventSlug/email-enviado" element={<EmailSuccessPage />} />
                  <Route path=":eventSlug" element={<EventPublicPage />} />
                  <Route
                    path="*"
                    element={
                      <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h6">Página não encontrada neste domínio</Typography>
                      </Box>
                    }
                  />
                </Routes>
              </MainLayout>
            }
          />
        </Routes>
      </AuthContextManager>
    );
  }

  // Rotas para Super Admin (admin.localhost)
  if (subdomain === 'admin') {
    return (
      <AuthContextManager>
        <Routes>
        {/* Login do Super Admin */}
        <Route path="login" element={<AuthLayout><AdminLoginPage /></AuthLayout>} />
        {/* Login do Contador */}
        <Route path="contador" element={<AuthLayout><ContadorLoginPage /></AuthLayout>} />
        <Route path="CONTADOR" element={<Navigate to="/contador" replace />} />
        
        {/* Rotas protegidas do Super Admin */}
        <Route path="*" element={
          <SuperAdminRoute>
            <SuperAdminMainLayout>
              <ContadorOnlyGuard>
              <Routes>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="parishes" element={<AdminParishes />} />
                <Route path="crm" element={<AdminProducerCrm />} />
                <Route path="parishes/pagarme/:parishId" element={<ParishPagarmeReview />} />
                <Route path="pagarme-review" element={<LegacyPagarmeReviewRedirect />} />
                {/* Rotas para eventos - aceitar com e sem /admin */}
                <Route path="admin/events/edit/:eventId" element={<AdminEditEventPage />} />
                <Route path="events/edit/:eventId" element={<AdminEditEventPage />} />
                <Route path="admin/events" element={<AdminEvents />} />
                <Route path="events" element={<AdminEvents />} />
                <Route path="payouts" element={<Navigate to="/transactions/repasses" replace />} />
                <Route path="transactions" element={<TransactionsLayout />}>
                  <Route index element={<Navigate to="saques" replace />} />
                  <Route path="saques" element={<Transactions />} />
                  <Route path="repasses" element={<AdminPayouts brandScope="platform" />} />
                  <Route path="zuadaria" element={<AdminPayouts brandScope="zuadaria" />} />
                </Route>
                <Route path="bulk-messages" element={<BulkMessages />} />
                <Route path="payment-queues" element={<PaymentQueuesLayout />}>
                  <Route index element={<PaymentQueues />} />
                  <Route path="ledger" element={<AdminLedger />} />
                </Route>
                <Route path="ledger" element={<Navigate to="/payment-queues/ledger" replace />} />
                <Route path="refunds" element={<Refunds />} />
                <Route path="invoice-issuance" element={<InvoiceIssuance />} />
                <Route path="contadores" element={<ContadoresAccessPage />} />
                <Route path="app-para-app" element={<AppParaApp />} />
                <Route path="custom-pages" element={<CustomPageInvitesPage />} />
                <Route path="contratos" element={<AdminLegalContracts />} />
                <Route path="reports" element={<AdminReports />} />
                <Route path="documentation" element={<DocumentationPage />} />
                <Route path="settings" element={<AdminSettings />} />
              </Routes>
              </ContadorOnlyGuard>
            </SuperAdminMainLayout>
          </SuperAdminRoute>
        } />
        </Routes>
      </AuthContextManager>
    );
  }

  // Rotas para Parish Admin (parish.localhost)
  if (subdomain === 'parish') {
    return (
      <AuthContextManager>
        <Routes>
        {/* Login do Parish Admin */}
        <Route path="login" element={<AuthLayout><LoginPage /></AuthLayout>} />
        <Route path="login-subparoquia" element={<AuthLayout><SubParishLoginPage /></AuthLayout>} />
        <Route path="acesso/:parishSlug" element={<AuthLayout><SectorAccessPage /></AuthLayout>} />
        <Route path="cadastro-paroquia" element={<AuthLayout wideMain><ParishRegistrationRequest /></AuthLayout>} />
        <Route path="cadastro-personalizado" element={<PersonalizedSignupPage />} />
        <Route path="recuperar-senha" element={<AuthLayout><ForgotPasswordPage /></AuthLayout>} />
        <Route path="resetar-senha/:token" element={<AuthLayout><ResetPasswordPage /></AuthLayout>} />
        <Route path="definir-senha/:token" element={<AuthLayout><SetPassword /></AuthLayout>} />
        
        {/* Rotas protegidas do Parish Admin */}
        <Route path="dashboard/*" element={<ParishAdminDashboardTree />} />
        
        {/* Festejos */}
        <Route path="/festejos/novo" element={
          <ParishAdminRoute>
            <FestojoFormContainer />
          </ParishAdminRoute>
        } />
        <Route path="/festejos/:festejoSlug/editar" element={<FestojoDemo />} />
        
        {/* Regularização Pagarme — link + login parish_admin */}
        <Route path="regularizar-pagarme" element={<RegularizarPagarmeParishPage />} />
        <Route path="assinar-contrato/:token" element={<ContractSignaturePage />} />
        <Route path="check-in" element={<EventCheckInPortalPage />} />

        {/* Redirecionar para dashboard se autenticado, senão para login */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </AuthContextManager>
    );
  }

  // Rotas para Client App (client.localhost ou sem subdomínio)
  return (
    <AuthContextManager>
      <Routes>
      {/* Rota explícita para evitar que solicitacao-enviada seja capturada por :eventSlug */}
      <Route path="check-in" element={<EventCheckInPortalPage />} />
      <Route path="solicitacao-enviada" element={<MainLayout><SolicitacaoEnviadaPage /></MainLayout>} />
      <Route path="p/:parishSlug" element={<LandingPublicPage />} />

      {/* Rotas públicas com layout principal */}
      <Route path="*" element={
        <MainLayout>
          <Routes>
            <Route index element={<HomePage />} />
            <Route path="pagamento-pendente/:registrationId" element={<PendingPaymentPage />} />
            <Route path="institucional" element={<AcutisInstitucionalPage />} />
            <Route path="politica-privacidade" element={<PoliticaPrivacidadePage />} />
            <Route path="termos-de-uso" element={<TermsOfService />} />
            <Route path="politica-de-privacidade" element={<PrivacyPolicy />} />
            <Route path="politica-de-reembolso" element={<RefundPolicy />} />
            <Route path="eventos/:id" element={<EventDetailsPage />} />
            <Route path=":eventSlug/inscricao" element={<EventRegistrationPage />} />
            <Route path=":eventSlug/produto/:productId" element={<ProductPurchasePage />} />
            <Route path=":eventSlug/suporte-email" element={<EmailSupportPage />} />
            <Route path=":eventSlug/email-enviado" element={<EmailSuccessPage />} />
            <Route path=":eventSlug" element={<EventPublicPage />} />
            <Route path="loja/:storeSlug" element={<StorePublicPage />} />
            <Route path="festejo/:festejoSlug" element={<FestojoPublicPage />} />
          </Routes>
        </MainLayout>
      } />

      {/* Rotas de autenticação */}
      <Route path="login" element={<AuthLayout><LoginPage /></AuthLayout>} />
      <Route path="login-subparoquia" element={<AuthLayout><SubParishLoginPage /></AuthLayout>} />
      <Route path="acesso/:parishSlug" element={<AuthLayout><SectorAccessPage /></AuthLayout>} />
      <Route path="cadastro-paroquia" element={<AuthLayout wideMain><ParishRegistrationRequest /></AuthLayout>} />
      <Route path="cadastro-personalizado" element={<PersonalizedSignupPage />} />
      <Route path="definir-senha/:token" element={<AuthLayout><SetPassword /></AuthLayout>} />
      <Route path="recuperar-senha" element={<AuthLayout><ForgotPasswordPage /></AuthLayout>} />
      <Route path="resetar-senha/:token" element={<AuthLayout><ResetPasswordPage /></AuthLayout>} />
      <Route path="preencher-dados-fiscais/:token" element={<InvoiceDataRequestPage />} />
      <Route path="assinar-contrato/:token" element={<ContractSignaturePage />} />
      <Route path="regularizar-pagarme" element={<RegularizarPagarmeParishPage />} />

      {/* Dashboard protegido — mesma árvore do painel parish (inclui Novidades / beta) */}
      <Route path="dashboard/*" element={<ParishAdminDashboardTree />} />

      {/* Rota de fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthContextManager>
  );
};

export default SubdomainRouter;
